import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as SubscriptionService from 'services/subscription';

export const useSubscriptionService = () => {
  const { token } = useSelector((store) => store.user_data);

  const buyFreemiumSubscription = async (options = {}) => {
    const { error, payload } = await SubscriptionService.manage('freemium', 'purchase', {
      ...options,
      token
    });

    if (error) {
      toast.error('Unable to purchase subscription at this time.');
    } else {
      toast.success('Subscription activated Successfully');
    }

    return { subscription: payload };
  };

  const cancelSubscription = async (id, options = {}) => {
    const { error, payload } = await SubscriptionService.manage(id, 'cancel', {
      ...options,
      token
    });

    if (error) {
      toast.error('Unable to cancel subscription at this time.');
      return null;
    }

    toast.success('Subscription Cancelled Successfully');
    return payload;
  };

  const determinePaymentDetails = ({
    currency,
    user_tenure,
    selected_pricing,
    tax_rates,
    renewal_period
  }) => {
    if (!currency.code || !selected_pricing || !renewal_period) return;

    const { discounts, prices } = selected_pricing;
    let cost = prices.find((p) => p.currency === currency.code)?.amount || 0;
    const {
      tenure,
      value: discount_rate,
      expires_at: discount_expiry
    } = discounts[renewal_period] || {};

    if (renewal_period === 'year') {
      cost *= 12;
    }

    const tax_rate = tax_rates[currency.code];
    const can_use_discount =
      (tenure === user_tenure || tenure === 'all') &&
      discount_expiry &&
      discount_expiry > Date.now();
    const discount = can_use_discount ? Number((cost * discount_rate) / 100) : 0;
    const discounted_price = Number(cost - discount);
    const tax = Number((discounted_price * Number(tax_rate)) / 100) || 0;
    const amount_to_pay = Number(discounted_price) + Number(tax);

    return {
      amount_to_pay,
      cost,
      discount,
      discount_rate,
      discounted_price,
      renewal_period,
      currency,
      tax,
      tax_rate
    };
  };

  const determineSubscriptionPlanClass = async (options) => {
    const { error, payload } = await SubscriptionService.read({ ...options, token });
    if (error) {
      return '';
    }

    const {
      data: [subscription]
    } = payload;

    if (!subscription) return 'flex';
    const contact_bracket = subscription?.metadata?.pricing?.contact_bracket;

    if (!contact_bracket || !contact_bracket.max || contact_bracket.max < 250000) return 'growth';
    return 'enterprise';
  };

  const fetchSubscription = async (options = {}) => {
    const { error, payload } = await SubscriptionService.read({ ...options, token });
    if (error) {
      return { subscriptions: [], size: 0 };
    }

    const {
      data: subscriptions,
      meta: { size }
    } = payload;
    return { subscriptions, size };
  };

  return {
    buyFreemiumSubscription,
    cancelSubscription,
    determinePaymentDetails,
    determineSubscriptionPlanClass,
    fetchSubscription
  };
};
